import React, { useEffect, useState } from "react";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider.tsx";
import { useEndpointProvider } from "../../../Providers/EndpointProvider";
import { useAuthProvider } from "../../../Providers/AuthProvider";
import { useWorkspacesProvider } from "../../../Providers/WorkspacesProvider";
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal";
import { MobergTheme } from "../../../Components/MobergThemes/MobergColors";
import { CircularProgress, Box, Typography, Tooltip } from "@mui/material";
import { io } from "socket.io-client";
import { MobergFontFamily } from "../../../Components/MobergFont/MobergFont";
import ModernMUITable from "../../../Components/ModernMUITable";
import { useSetRecoilState } from "recoil";
import { currentPatientFileInfoAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile";
import { DataSource } from "../../../Pages/Data/Visualize/DataReview/Types/DataSource";
import { AlertsStatusDisplay } from "../components/AlertsStatusDisplay";
import { useModalProvider } from "../../../Providers/ModalProvider";
import { AlertsList } from "../../../Pages/Data/Visualize/DataReview/Components/React/AlertsList";
import { GATEWAY_STATUS } from "../constants";
import StatusBadge from "../components/StatusBadge";
import MobergSnackbar from "../../../Components/MobergSnackbar/MobergSnackbar";

export default function RMGatewayMonitoring(props) {
    const { gatewayId: gatewayIdProp, onStop: onStopProp, isModal = false, exposeHandlers } = props;
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const { LINKS } = useBackendLinksProvider();
    const endpointProvider = useEndpointProvider();
    const { token } = useAuthProvider();
    const { selectedWorkspace } = useWorkspacesProvider();
    const [monitoringStatus, setMonitoringStatus] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [socket, setSocket] = useState(null);
    const gatewayId = gatewayIdProp || "Not Assigned";
    const [devices, setDevices] = useState([]);
    const [dataObjectId, setDataObjectId] = useState(null);
    const setPatientFileInfo = useSetRecoilState(currentPatientFileInfoAtom);
    const { createModal } = useModalProvider();

    const handleViewAlerts = () => {
        createModal(<AlertsList />);
    };

    const fetchGatewayInfo = async () => {
        setIsLoading(true);
        try {
            const res = await endpointProvider.get(LINKS.AGGREGATOR.GET_GATEWAY_INFO, {
                gateway_id: parseInt(gatewayId, 10)
            });
            setData(res);
            if (res?.gateway?.status) {
                setMonitoringStatus(res.gateway.status);
            }
            if (res?.gateway?.devices) {
                setDevices(res.gateway.devices);
            }
            // Capture data_object_id for alerts
            if (res?.data_object_id) {
                setDataObjectId(res.data_object_id);
                // Update Recoil atom for AlertsProvider
                setPatientFileInfo(previous => ({
                    ...previous,
                    dataSourceMap: new Map(previous.dataSourceMap).set(
                        DataSource.CURRENT_PATIENT, 
                        res.data_object_id
                    )
                }));
            }
            return res;
        } catch (err) {
            console.error("Error fetching gateway info:", err);
            setError('Failed to load device information. Please try again.');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const openGatewayWebsocket = (gatewayId) => {
        // Close existing socket if any
        if (socket) {
            socket.disconnect();
        }

        const newSocket = io(`${LINKS.QUERY}/gateways`, { 
            transports: ["websocket"],
            auth: {
                token: token ?? "",
                workspace_name: selectedWorkspace ?? ""
            }
        });
        
        newSocket.emit("track", gatewayId);
        
        newSocket.on("gateway_update", (data) => {
            // Update monitoring status directly from websocket data
            if (data?.status) {
                setMonitoringStatus(data.status);
                // Also update the data state to reflect the new status
                setData(prevData => ({
                    ...prevData,
                    gateway: {
                        ...prevData?.gateway,
                        status: data.status
                    }
                }));
                
                // If status is STOPPED, wait 1 second and call callback
                if (data.status === "STOPPED") {
                    setTimeout(() => {
                        if (onStopProp) {
                            onStopProp();
                        }
                    }, 1000);
                }
            }
        });

        newSocket.on("device_update", (data) => {
            setDevices(prevDevices => {
                const updatedDevices = [...prevDevices];
                const deviceIndex = updatedDevices.findIndex(device => device.device_id === data.id);
                if(deviceIndex !== -1){
                    data.device_id = data.id;
                    // Merge the update with existing device data to preserve fields like wave_priority_list
                    updatedDevices[deviceIndex] = {
                        ...updatedDevices[deviceIndex],
                        ...data
                    };
                }
                return updatedDevices;
            })
        });

        setSocket(newSocket);
    };

    // Cleanup socket on component unmount
    useEffect(() => {
        return () => {
            if (socket) {
                socket.disconnect();
            }
        };
    }, [socket]);

    useEffect(() => {
        const fetchData = async () => {
            const info = await fetchGatewayInfo();
            if (info) {
                setData(info);
                let wsGatewayId = info.gateway?.gateway_id;
                if(wsGatewayId){
                    openGatewayWebsocket(wsGatewayId);
                }
                else{
                    console.error("No gateway ID found");
                }
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gatewayId]);

    // Expose handlers to parent modal when in modal mode
    useEffect(() => {
        if (isModal && exposeHandlers) {
            exposeHandlers({
                onToggle: handleToggleMonitoring,
                onStop: handleStopMonitoring,
                toggleButtonText: monitoringStatus === GATEWAY_STATUS.ACTIVE || monitoringStatus === GATEWAY_STATUS.STARTING ? "Pause" : "Resume",
                isToggleDisabled: isMonitoringDisabled(),
                isStopDisabled: isMonitoringDisabled()
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModal, exposeHandlers, monitoringStatus, isLoading, data]);

    const handleToggleMonitoring = async () => {
        if (isLoading) return;

        const isPausing = monitoringStatus === GATEWAY_STATUS.ACTIVE;
        const actionLabel = isPausing ? "Pause" : "Resume";

        createModal(<ConfirmationModal
            title={`${actionLabel} Monitoring`}
            description={`Are you sure you want to ${actionLabel.toLowerCase()} monitoring for this device?`}
            confirmButton={{
                text: actionLabel,
                theme: MobergTheme.BLUE,
                onClick: async () => {
                    setIsLoading(true);
                    try {
                        if (monitoringStatus === GATEWAY_STATUS.STOPPED) {
                            setError("Cannot toggle monitoring when status is STOPPED");
                            return;
                        }

                        setMonitoringStatus(prev => prev === GATEWAY_STATUS.ACTIVE ? GATEWAY_STATUS.PAUSED : GATEWAY_STATUS.ACTIVE);

                        await endpointProvider.post(LINKS.AGGREGATOR.TOGGLE_MONITORING, {
                            gateway_id: parseInt(gatewayId, 10)
                        });
                    } catch (err) {
                        console.error("Error toggling monitoring:", err);
                        if (err.message?.includes("STOPPED")) {
                            setError("Cannot toggle monitoring when status is STOPPED");
                        } else {
                            setError("Failed to toggle monitoring. Please try again.");
                        }
                    } finally {
                        setIsLoading(false);
                    }
                }
            }}
        />);
    };

    const handleStopMonitoring = async () => {
        if (isLoading) return;

        createModal(<ConfirmationModal
            title="Stop Monitoring"
            description="Are you sure you want to stop monitoring for this device? This action cannot be undone."
            confirmButton={{
                text: "Stop Monitoring",
                theme: MobergTheme.RED,
                onClick: async () => {
                    setIsLoading(true);
                    try {
                        await endpointProvider.post(LINKS.AGGREGATOR.STOP_MONITORING, {
                            gateway_id: parseInt(gatewayId, 10)
                        });
                    } catch (err) {
                        console.error("Error stopping monitoring:", err);
                        setError("Failed to stop monitoring. Please try again.");
                    } finally {
                        setIsLoading(false);
                    }
                }
            }}
        />);
    };

    const isMonitoringDisabled = () => {
        return isLoading || data?.gateway?.status === GATEWAY_STATUS.STOPPED || data?.gateway?.status === GATEWAY_STATUS.STARTING || data?.gateway?.status === GATEWAY_STATUS.PAUSING || data?.gateway?.status === GATEWAY_STATUS.STOPPING;
    }

    const calculateDuration = (startTime) => {
        if (!startTime) return "Unknown";

        const start = new Date(startTime);
        const now = new Date();
        const diff = now - start;

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) return `${days} days ${hours} hrs ${minutes} mins`;
        if (hours > 0) return `${hours} hrs ${minutes} mins`;
        return `${minutes} mins`;
    };

    // Format wave priority list for display
    const formatMeasurements = (wavePriorityList) => {
        // Handle empty or invalid data
        if (!wavePriorityList || !Array.isArray(wavePriorityList) || wavePriorityList.length === 0) {
            return { displayText: "-", fullText: "" };
        }

        // Get the first element and split by semicolon
        const measurementsString = wavePriorityList[0];
        if (!measurementsString || typeof measurementsString !== "string") {
            return { displayText: "-", fullText: "" };
        }

        const measurements = measurementsString.split(";").map(m => m.trim()).filter(m => m);
        
        if (measurements.length === 0) {
            return { displayText: "-", fullText: "" };
        }

        // Full text for tooltip
        const fullText = measurements.join(", ");

        // Display text: show first 6 and count the rest
        if (measurements.length <= 6) {
            return { displayText: fullText, fullText };
        } else {
            const firstSix = measurements.slice(0, 6).join(", ");
            const remaining = measurements.length - 6;
            return { 
                displayText: `${firstSix} and ${remaining} more`, 
                fullText 
            };
        }
    };

    // Table columns configuration
    const columns = [
        {
            field: "status",
            headerName: "Status",
            minWidth: 100,
            flex: 0.15,
            renderCell: (params) => {
                return <StatusBadge status={params.row.status} />;
            }
        },
        {
            field: "device_type",
            headerName: "Device Name",
            minWidth: 150,
            flex: 0.2,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "15px", color: "var(--color-text-primary)" }}>
                        {params.row.device_type || "Unknown"}
                    </Typography>
                </Box>
            )
        },
        {
            field: "measurement",
            headerName: "Measurement",
            minWidth: 200,
            flex: 0.4,
            sortable: false,
            renderCell: (params) => {
                const { displayText, fullText } = formatMeasurements(params.row.wave_priority_list);
                
                return (
                    <Tooltip 
                        title={fullText || ""} 
                        arrow 
                        placement="top"
                        disableHoverListener={!fullText}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                            <Typography variant="body1" sx={{ fontSize: "15px", color: "var(--text-secondary-color)" }}>
                                {displayText}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            }
        }


    ];

    // Table toolbar configuration
    const tableToolbarProps = {
        createButton: { isShow: false },
        deleteButton: { isShow: false },
        filterButton: { isShow: false },
        exportButton: { isShow: false },
        searchInput: { isShow: false },
        columnButton: { isShow: false },
        mainTable: false
    };

    return (
        <Box sx={{ 

            minHeight: isModal ? "auto" : "calc(100vh - 60px)", 
            height: "100%",
            display: "flex",
            flexDirection: "column",
            
        }}>
            <Box sx={{ 
                flex: 1, 
                paddingTop: '16px',
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: "var(--color-surface-primary)",
            }}>

                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                    </Box> 
                ) : (
                    <Box sx={{ 
                        
                        margin: isModal ? '0' : '20px 0',
                        marginBottom: '16px',
                        overflow: 'hidden',
                        maxWidth: '800px',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        flex: 1
                   
                    }}>
                        {/* MCP device information Section */}
                        <Box sx={{ padding: '16px 16px', backgroundColor: 'var(--color-surface-panel)', borderRadius: '4px' }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 600, 
                                color: "var(--color-text-primary)", 
                                fontFamily: MobergFontFamily.HEADER,
                                fontSize: "16px",
                                marginBottom: 1.5
                            }}>
                                MCP Device
                            </Typography>
                            
                            <Box sx={{ 
                                display: "grid", 
                                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                gap: 2,
                                marginBottom: 2.5
                            }}>
                                <Box>
                                    <Typography variant="body2" sx={{ color: "var(--text-secondary-color)", marginBottom: 0.75, fontSize: "13px" }}>
                                        Name
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "15px", color: "var(--color-text-primary)" }}>
                                        {data?.gateway_name || "Unknown"}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ color: "var(--text-secondary-color)", marginBottom: 0.75, fontSize: "13px" }}>
                                        Status
                                    </Typography>
                                    <StatusBadge status={data?.gateway?.status} />
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ color: "var(--text-secondary-color)", marginBottom: 0.75, fontSize: "13px" }}>
                                        Started
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "15px", color: "var(--color-text-primary)" }}>
                                        {data?.start_time
                                            ? new Date(data.start_time).toLocaleString('en-GB', { 
                                                day: '2-digit', 
                                                month: 'short', 
                                                year: 'numeric', 
                                                hour: 'numeric', 
                                                minute: '2-digit',
                                                hour12: true 
                                            }).replace(',', ' -').replace(/am|pm/gi, (match) => match.toUpperCase())
                                            : "Unknown"}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: "right" }}>
                                    <Typography variant="body2" sx={{ color: "var(--text-secondary-color)", marginBottom: 0.75, fontSize: "13px" }}>
                                        Duration
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "15px", color: "var(--color-text-primary)" }}>
                                        {calculateDuration(data?.start_time)}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="body2" sx={{ color: "var(--text-secondary-color)", marginBottom: 0.75, fontSize: "13px" }}>
                                        Patient ID
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "15px", color: "var(--color-text-primary)" }}>
                                        {data?.patient?.patient_id || "Not Available"}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ color: "var(--text-secondary-color)", marginBottom: 0.75, fontSize: "13px" }}>
                                        Patient name
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "15px", color: "var(--color-text-primary)" }}>
                                        {data?.patient?.patient_name?.trim() ? data.patient.patient_name : "Unknown"}
                                    </Typography>
                                </Box>
                               
                            </Box>

                            {/* Alert Status Section */}
                            {dataObjectId && <AlertsStatusDisplay onViewAlertsClick={handleViewAlerts} monitoringStatus={monitoringStatus} />}
                            
                        </Box>

                        {/* Connected device details Section */}
                        <Box sx={{ padding: '16px 16px', backgroundColor: 'var(--color-surface-panel)', borderRadius: '4px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                                <Typography variant="h6" sx={{ 
                                    fontWeight: 600, 
                                    color: "var(--color-text-primary)", 
                                    fontFamily: MobergFontFamily.HEADER,
                                    fontSize: "16px"
                                }}>
                                    Third-party Devices
                                </Typography>
                                <Typography sx={{ 
                                    fontSize: "14px",
                                    color: "var(--color-text-primary)",
                                    fontWeight: 500,
                                    textDecoration: "underline"
                                }}>
                                    {devices.length} connected
                                </Typography>
                            </Box>
                            
                            <Box sx={{ flex: 1, minHeight: 0 }}>
                                <ModernMUITable
                                    columns={columns}
                                    tableData={devices}
                                    rowCount={devices.length}
                                    tableToolbarProps={tableToolbarProps}
                                    showToolbar={false}
                                    supportsPageSize={false}
                                    pageSizeOptions={[]}
                                    isRowSelectable={() => false}
                                    isCheckboxSelection={false}
                                    checkboxSelection={false}
                                    height="100%"
                                    paginationMode="client"
                                    sortingMode="client"
                                    getRowId={(row) => row.device_id}
                                    hideFooter={true}
                                    overrideStyles={{
                                        '& .MuiDataGrid-columnHeader': {
                                            fontSize: '13px'
                                        },
                                        '& .MuiDataGrid-columnHeaderTitle': {
                                            fontSize: '13px',
                                            fontWeight: 400,
                                            color: 'var(--text-secondary-color)'
                                        }
                                    }}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { page: 0, pageSize: 100 }
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Error Notification */}
            <MobergSnackbar
                open={!!error}
                onClose={() => setError(null)}
                message={error}
                severity="error"
            />
        </Box>
    );
}

